import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import {
  FormControl,
  ReactiveFormsModule,
  Validators,
  NonNullableFormBuilder,
} from '@angular/forms';
import { TuiDialogContext, TuiButton } from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { TranslatePipe } from '@ngx-translate/core';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { TextCopierComponent } from '@core/components';
import { RequestsApiService } from '@core/services/request/requests-api.service';
import { TemporaryAccessCredentials, RolesProfile } from '@core/types';
import { catchError, finalize, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'x-temporary-access-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TuiButton,
    TranslatePipe,
    TextCopierComponent,
  ],
  templateUrl: './temporary-access-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemporaryAccessDialogComponent {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly requestsApiService = inject(RequestsApiService);
  private readonly translateService = inject(OutkeepTranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly context =
    inject<
      TuiDialogContext<
        TemporaryAccessCredentials | null,
        { requestIds: number[] }
      >
    >(POLYMORPHEUS_CONTEXT);

  readonly requestIds = this.context.data.requestIds;
  readonly loadingProfiles = signal(true);
  readonly submitting = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly rolesProfiles = signal<RolesProfile[]>([]);
  readonly result = signal<TemporaryAccessCredentials | null>(null);
  readonly defaultExpiresAt = this.getDefaultExpiresAt();

  readonly form = this.fb.group({
    rolesProfileId: new FormControl<number | null>(null, {
      validators: [Validators.required],
    }),
    expiresAt: this.fb.control(this.defaultExpiresAt, {
      validators: [Validators.required],
    }),
  });

  constructor() {
    this.requestsApiService
      .getTemporaryAccessRolesProfiles$()
      .pipe(
        catchError(() => {
          this.errorMessage.set(
            this.translateService.instant(
              'temporary_access_dialog.error_load_profiles',
            ),
          );

          return of([] as RolesProfile[]);
        }),
        finalize(() => this.loadingProfiles.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(profiles => {
        this.rolesProfiles.set(profiles);

        if (profiles.length === 1) {
          this.form.controls.rolesProfileId.setValue(profiles[0].id);
        }
      });
  }

  close(): void {
    this.context.completeWith(null);
  }

  submit(): void {
    this.errorMessage.set(null);

    if (this.form.invalid) {
      this.form.markAllAsTouched();

      return;
    }

    const rolesProfileId = Number(this.form.controls.rolesProfileId.value);
    const expiresAt = this.form.controls.expiresAt.value;

    if (!rolesProfileId || !expiresAt) {
      this.errorMessage.set(
        this.translateService.instant('temporary_access_dialog.error_fill_all'),
      );

      return;
    }

    this.submitting.set(true);

    this.requestsApiService
      .createTemporaryAccess$({
        rolesProfileId,
        expiresAt,
        requestIds: this.requestIds,
      })
      .pipe(
        catchError(() => {
          this.errorMessage.set(
            this.translateService.instant(
              'temporary_access_dialog.error_create',
            ),
          );

          return of(null);
        }),
        finalize(() => this.submitting.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(result => {
        if (!result) {
          return;
        }

        this.result.set(result);
      });
  }

  done(): void {
    this.context.completeWith(this.result());
  }

  private getDefaultExpiresAt(): string {
    const date = new Date();

    date.setDate(date.getDate() + 14);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}
